import { axios, EQ_OPERATOR, AND_SUB_SEPARATOR } from '@argon/app/axios'
import { slideItem, moveItem, makeCardURL } from '../utils'
import { orderBy } from 'lodash/collection'
import { toFilterString, stampItem } from '@argon/app/helpers'

const NAMESPACE = 'entities'
const KANBAN = 'kanban'
const TABLE = 'table'

const state = {
  displayType: null,
  boardItem: null,
  include: null,
  filters: {},
  tableItems: [],
  archivedItems: [],
  tableItemsCount: null,
  tablePagination: {},
  kanbanItems: {},
  collectionURL: null,
  entityFilter: null,
  principals: [],
  title: '',
  transitions: [],
  anonymous: process.env.VUE_APP_ARGON_ANONYMOUS_ATTRIBUTION
    ? process.env.VUE_APP_ARGON_ANONYMOUS_ATTRIBUTION === 'true'
    : false
}

const mutations = {
  SET_PRINCIPALS: (state, payload) => {
    state.principals = payload
  },
  SET_INCLUDE: (state, payload) => {
    state.include = payload
  },
  SET_STARTING_STATES: (state, payload) => {
    state.startingStates = payload
  },
  SET_COLLECTION_URL: (state, payload) => {
    state.collectionURL = payload
  },
  SET_BOARD_ITEM: (state, payload) => {
    state.boardItem = payload
  },
  SET_TABLE_PAGINATION: (state, payload) => {
    state.tablePagination = payload
  },
  SET_TABLE_ITEMS: (state, payload) => {
    state.tableItems = payload
  },
  SET_TABLE_ITEMS_COUNT: (state, payload) => {
    state.tableItemsCount = payload
  },
  SET_KANBAN: (state, payload) => {},
  SET_KANBAN_ITEMS: (state, payload) => {
    state.kanbanItems = payload
  },
  CLEAR_KANBAN: (state, payload) => {
    state.kanbanItems = {}
  },
  SET_BOARD_ARCHIVED_ITEMS: (state, payload) => {
    state.archivedItems = payload
  },
  SET_DISPLAY_TYPE(state, payload) {
    state.displayType = payload
  }
}

const actions = {
  setDisplayType: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit('SET_DISPLAY_TYPE', payload)
      resolve(true)
    })
  },
  transit: ({}, payload) => {
    const { boardRef, after_id, itemId, transition } = payload
    let url = `/boards/${boardRef}/items/${itemId}/transit`
    let postData = {
      transition: transition
    }
    if (after_id) {
      postData['after_id'] = after_id
    }
    return new Promise((resolve, reject) => {
      axios({
        url,
        data: postData,
        method: 'POST'
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  shift: ({}, payload) => {
    const { boardRef, after_id, itemId } = payload
    let data = after_id === 0 ? {} : { after_id }
    let url = `/boards/${boardRef}/items/${itemId}/shift`
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  setToPosition: ({ commit, state }, payload) => {
    const { item, position } = payload
    return new Promise((resolve, reject) => {
      let boardItems = state.tableItems.map((wrp) => (wrp.entity === item.entity ? { ...wrp, position } : wrp))
      commit('SET_TABLE_ITEMS', boardItems)
      resolve({ itemId: item.entity.id })
    })
  },
  slideEntity: ({ commit, state }, payload) => {
    return new Promise((resolve, reject) => {
      slideItem(state.kanbanItems, payload).then(({ kanbanItems, after_id }) => {
        commit('SET_KANBAN_ITEMS', kanbanItems)
        resolve({ itemId: payload.item.entity.id, after_id })
      })
    })
  },
  moveEntity: ({ commit, state }, payload) => {
    const { item, transition } = payload
    return new Promise((resolve, reject) => {
      moveItem(state.kanbanItems, payload).then(({ kanbanItems, after_id }) => {
        commit('SET_KANBAN_ITEMS', kanbanItems)
        resolve({
          itemId: item.entity.id,
          after_id,
          transition: transition.name
        })
      })
    })
  },
  listBoardItems: ({ commit, state, rootState }, payload) => {
    const { boardRef, pager, include, counts } = payload
    let localInclude = include || state.include
    let pagerParams = pager || state.tablePagination
    const filters = toFilterString(rootState.app.appliedFilters[boardRef])
    let params = { counts, include: localInclude, filters, ...pagerParams }
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${boardRef}/items`,
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_TABLE_ITEMS', data.items)
          commit('SET_TABLE_ITEMS_COUNT', data.count)
          commit('SET_TABLE_PAGINATION', pagerParams)
          commit('SET_INCLUDE', localInclude)
          dispatch('app/setAppliedFilter', { boardRef, filterObject: localFilters }, { root: true })
          resolve(true)
        })
        .catch(reject)
    })
  },
  clearEntities: ({ commit }) => {
    commit('SET_TABLE_ITEMS', [])
    commit('SET_TABLE_PAGINATION', {})
    commit('SET_INCLUDE', 'users')
  },
  listBoardItemsForState: ({ commit, state, rootState }, payload) => {
    const { boardRef, flowState, pager, stateAttr, counts, include } = payload
    const filters = toFilterString(rootState.app.appliedFilters[boardRef])
    let localInclude = include || state.include
    let stateFilter = `${stateAttr}:${EQ_OPERATOR}:${flowState}`
    let reFilters = filters ? `${filters}${AND_SUB_SEPARATOR}${stateFilter}` : stateFilter
    let pagerParams = pager || {}
    let filterParams = { filters: reFilters }
    let params = { counts, include: localInclude, ...filterParams, ...pagerParams }
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${boardRef}/items`,
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          let kanban = { ...state.kanbanItems }
          kanban[flowState] = orderBy(data.items, ['position'], ['asc'])
          commit('SET_KANBAN_ITEMS', kanban)
          commit('SET_KANBAN', payload)
          commit('SET_INCLUDE', localInclude)
          resolve(true)
        })
        .catch(reject)
    })
  },
  clearKanbanEntities: ({ commit }, payload) => {
    commit('CLEAR_KANBAN', payload)
  },
  listArchivedEntities: ({ commit }, payload) => {
    const { id } = payload
    let params = { include: 'only_archived' }
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${id}/items`,
        params: params,
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_BOARD_ARCHIVED_ITEMS', data.items)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  clearArchivedEntities: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_BOARD_ARCHIVED_ITEMS', [])
      resolve(true)
    })
  },
  createEntity: ({ commit }, payload) => {
    const { url, data, flowState, boardRef } = payload
    let fullData = { ...data, state: flowState }
    return new Promise((resolve, reject) => {
      return axios({
        url,
        data: fullData,
        method: 'POST'
      })
        .then((resp) => {
          resolve(resp.data)
        })
        .catch(reject)
    })
  },
  createAddons: ({ dispatch }, payload) => {
    const { baseURL, items } = payload
    return new Promise((resolve, reject) => {
      let promises = items.map((item) =>
        dispatch(
          'addons/createEntityAddon',
          {
            entityURL: baseURL,
            addon: item.name,
            data: item.data
          },
          { root: true }
        )
      )
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  createAndAdd: ({ dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      dispatch('createEntity', payload)
        .then((entity) => {
          return dispatch('createAddons', { baseURL: entity.uris.self, items: payload.addons }).then(() => entity)
        })
        .then((entity) => {
          return dispatch('addEntityToBoard', { ...payload, entityId: entity.id }).then(() => entity)
        })
        .then(resolve)
    })
  },
  getBoardItem: ({ commit, state }, payload) => {
    const { url, include } = payload
    let localInclude = include || state.include
    return new Promise((resolve, reject) => {
      axios({
        url,
        params: { include: localInclude },
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_BOARD_ITEM', data)
          commit('SET_INCLUDE', localInclude)
          resolve(data)
        })
        .catch(reject)
    })
  },
  patchEntity: ({ commit, state }, payload) => {
    const { url, data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          let entity = { ...state.boardItem.entity, ...data }
          let boardItem = { ...state.boardItem, entity }
          commit('SET_BOARD_ITEM', boardItem)
          resolve(data)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  archiveEntities: ({}, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/archive`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  restoreEntities: ({}, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/restore`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteEntity: ({}, url) => {
    return new Promise((resolve, reject) => {
      axios({
        url,
        method: 'DELETE'
      })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteEntities: ({}, paths) => {
    return new Promise((resolve, reject) => {
      let promises = paths.map((url) => axios({ url, method: 'DELETE' }))
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  clearEntity: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_BOARD_ITEM', null)
      resolve(true)
    })
  },
  callTheRightFetcher: ({ dispatch }, payload) => {
    const { flowState } = payload
    const cb = flowState ? () => dispatch('listBoardItemsForState', payload) : () => dispatch('listBoardItems', payload)
    return new Promise((resolve, reject) => {
      cb().then(resolve).catch(reject)
    })
  },
  addEntityToBoard: ({ dispatch }, payload) => {
    const { boardRef, entityId } = payload
    let url = `/boards/${boardRef}/items`
    return new Promise((resolve, reject) => {
      axios({
        url,
        data: { id: entityId },
        method: 'POST'
      })
        .then(() => {
          return dispatch('callTheRightFetcher', payload)
        })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  listPrincipals: ({ commit }, baseURL) => {
    let url = `${baseURL}/principals`
    return new Promise((resolve, reject) => {
      axios({ url, method: 'GET', params: { include: 'attribution,type,user' } })
        .then(({ data }) => {
          commit('SET_PRINCIPALS', data.items)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  addPrincipals: ({ dispatch }, payload) => {
    const { principalIds, attribution, baseURL } = payload
    let promises = principalIds.map((principal_id) => {
      let url = `${baseURL}/principals`
      let data = { principal_id, attribution }
      return axios({
        url,
        data,
        method: 'POST'
      })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return dispatch('listPrincipals', baseURL)
        })
        .then(() => {
          dispatch('listPrincipals', baseURL)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  deletePrincipals: ({ dispatch }, payload) => {
    const { principalIds, baseURL } = payload
    let promises = principalIds.map((principal_id) =>
      axios({
        url: `${baseURL}/principals/${principal_id}`,
        method: 'DELETE'
      })
    )
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return dispatch('listPrincipals', baseURL)
        })
        .then(() => {
          dispatch('listPrincipals', baseURL)
        })
        .then(resolve)
        .catch(reject)
    })
  },
  // Actions
  resolveAction: ({ commit }, payload) => {
    const { boardRef, entityId, actionId } = payload
    let extra = { card_url: makeCardURL(boardRef, entityId) }
    let url = `/boards/${boardRef}/items/${entityId}/resolve`
    return new Promise((resolve, reject) => {
      axios({
        url,
        data: {
          action: actionId,
          extra
        },
        method: 'POST'
      })
        .then(({ data }) => {
          commit('SET_BOARD_ITEM', data)
          resolve(data)
        })
        .catch(reject)
    })
  },
  dismissAction: ({ commit }, payload) => {
    const { boardRef, entityId, actionId } = payload
    let url = `/boards/${boardRef}/items/${entityId}/dismiss`
    return new Promise((resolve, reject) => {
      axios({
        url,
        data: {
          action: actionId
          // extra,
        },
        method: 'POST'
      })
        .then(({ data }) => {
          commit('SET_BOARD_ITEM', data)
          resolve(data)
        })
        .catch(reject)
    })
  }
}

const getters = {
  archivedItems: (state) => state.archivedItems,
  tableItems: (state) => state.tableItems,
  kanbanBoardItems: (state) => (flowState) => state.kanbanItems[flowState] || [],
  title: (state) => state.title,
  displayType: (state) => state.displayType,
  entity: (state) => (state.boardItem ? state.boardItem.entity : null)
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
export { NAMESPACE, KANBAN, TABLE }
